import { useState, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  User,
  KeyRound,
  Shield,
  Monitor,
  Settings,
  LogOut,
  ChevronDown,
} from 'lucide-react';
import { useAuth } from '@/features/auth/hooks/useAuth';
import { cn } from '@/shared/lib/cn';

interface MenuItem {
  icon: React.ElementType;
  label: string;
  path?: string;
  onClick?: () => void;
  variant?: 'default' | 'danger';
}

export function UserMenu() {
  const [isOpen, setIsOpen] = useState(false);
  const menuRef = useRef<HTMLDivElement>(null);
  const navigate = useNavigate();
  const { user, logout } = useAuth();

  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (menuRef.current && !menuRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    }
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const menuItems: MenuItem[] = [
    { icon: User, label: 'My Profile', path: '/profile' },
    { icon: KeyRound, label: 'Change Password', path: '/profile/change-password' },
    { icon: Shield, label: 'Security', path: '/profile/security' },
    { icon: Monitor, label: 'Sessions', path: '/profile/sessions' },
    { icon: Settings, label: 'Preferences', path: '/profile/preferences' },
    { icon: LogOut, label: 'Logout', onClick: () => logout(), variant: 'danger' },
  ];

  const initials = user
    ? `${user.firstName.charAt(0)}${user.lastName.charAt(0)}`.toUpperCase()
    : '?';

  const displayName = user ? `${user.firstName} ${user.lastName}` : 'User';
  const userEmail = user?.email ?? '';

  return (
    <div ref={menuRef} className="relative">
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="flex items-center gap-2 rounded-lg p-1.5 transition-colors hover:bg-accent"
        aria-label="User menu"
        aria-expanded={isOpen}
      >
        <div className="flex h-8 w-8 items-center justify-center rounded-full bg-primary text-xs font-semibold text-primary-foreground">
          {initials}
        </div>
        <div className="hidden text-left md:block">
          <p className="text-sm font-medium leading-tight">{displayName}</p>
          <p className="text-xs text-muted-foreground">{userEmail}</p>
        </div>
        <ChevronDown
          className={cn(
            'hidden h-4 w-4 text-muted-foreground transition-transform md:block',
            isOpen && 'rotate-180',
          )}
        />
      </button>

      {isOpen && (
        <div className="absolute right-0 top-full mt-1 w-56 overflow-hidden rounded-lg border bg-card shadow-lg animate-in fade-in-0 zoom-in-95">
          {/* User header */}
          <div className="border-b px-4 py-3">
            <p className="text-sm font-medium">{displayName}</p>
            <p className="text-xs text-muted-foreground">{userEmail}</p>
          </div>

          {/* Menu items — separate scrollable wrapper so this can't run
              off-screen if the list grows; the header above stays put. */}
          <div className="max-h-[60vh] overflow-y-auto p-1">
            {menuItems.map((item, index) => {
              const Icon = item.icon;
              const isDanger = item.variant === 'danger';
              const isLast = index === menuItems.length - 1;

              return (
                <div key={item.label}>
                  {isLast && <div className="my-1 border-t" />}
                  <button
                    onClick={() => {
                      setIsOpen(false);
                      if (item.onClick) item.onClick();
                      if (item.path) navigate(item.path);
                    }}
                    className={cn(
                      'flex w-full items-center gap-3 rounded-md px-3 py-2 text-sm transition-colors',
                      isDanger
                        ? 'text-destructive hover:bg-destructive/10'
                        : 'text-foreground hover:bg-accent',
                    )}
                  >
                    <Icon className="h-4 w-4 shrink-0" />
                    <span>{item.label}</span>
                  </button>
                </div>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}
